import { PLACE_RESULT_TYPE } from '@web-platform/shared-universal-constants/src/places';
import {
  getPlacesResponseBody,
  isPlacesResponseError,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/radarPlacesResponse';
import { logOperationalEvent } from 'saddlebag-logger';

import type { AdvertTrackingInfo, TrackingContext } from '@skyscanner/strevda-tracking';
import type { SkyscannerAdvertsAdsretrievalserviceV1alpha1Response as AdsCapabilityResponse } from '@skyscanner-internal/ads-retrieval-service-web-http-client';
import type { RadarResponsePayload } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarResponsePayloadTypes';
import type { LocationExplorerResponse } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarUnfocusedSearch';

type GetAdsResponse = AdsCapabilityResponse.GetAdsResponse;
type AdsTrackingContext = AdsCapabilityResponse.TrackingContext;
type AdsTrackingPixels = AdsCapabilityResponse.TrackingPixels;
type StrevdaPixels = AdvertTrackingInfo['pixels'];

export const extractCombinedExploreAdvertsTrackingInfo = (
  response: RadarResponsePayload,
): AdvertTrackingInfo[] | null => {
  try {
    if (isPlacesResponseError(response)) return null;

    const body = getPlacesResponseBody(response);

    if (!body || !('everywhereDestination' in body) || !body.everywhereDestination.results) {
      return null;
    }
    const combineExploreAdverts: AdvertTrackingInfo[] = [];
    body.everywhereDestination.results.forEach((result, index) => {
      if (result.type === PLACE_RESULT_TYPE.LOCATION && result.content.inlineCreative) {
        combineExploreAdverts.push({
          creative: result.content.inlineCreative.creativeFields,
          pixels: result.content.inlineCreative.trackingPixels,
          trackingContext: result.content.inlineCreative.trackingContext,
          position: index,
        });
      } else if (
        result.type === PLACE_RESULT_TYPE.ADVERT &&
        result.content.creative?.type &&
        ['STANDARD_DISPLAY_ADVERT', 'DOUBLE_DISPLAY_ADVERT'].includes(result.content.creative.type)
      ) {
        combineExploreAdverts.push({
          creative: result.content.creative.fields,
          pixels: result.content.trackingPixels,
          trackingContext: result.content.trackingContext,
        });
      }
    });
    return combineExploreAdverts.length > 0 ? combineExploreAdverts : null;
  } catch (error) {
    logOperationalEvent({
      eventName: 'adverts_logging_error',
      error: String(error),
      errorType: 'Error while extracting combine explore adverts',
    });
    return null;
  }
};
export const transformDestinationExplorerToAdvertTrackingInfo = (
  destinationExplorer: LocationExplorerResponse,
): AdvertTrackingInfo => ({
  creative: destinationExplorer.creativeFields,
  pixels: destinationExplorer.trackingPixels,
  trackingContext: destinationExplorer.trackingContext,
});

const transformAdsCapabilityTrackingPixels = (
  trackingPixels: AdsTrackingPixels | undefined,
): StrevdaPixels => {
  const pixels: StrevdaPixels = {
    impression: trackingPixels?.impression ?? [],
  };

  if (trackingPixels?.click) {
    pixels.click = trackingPixels.click;
  }

  if (trackingPixels?.view) {
    pixels.view = trackingPixels.view;
  }

  if (trackingPixels?.engage) {
    pixels.engage = trackingPixels.engage;
  }

  return pixels;
};

const transformAdsCapabilityTrackingContext = (
  trackingContext: AdsTrackingContext | undefined,
): TrackingContext | null => {
  if (!trackingContext?.idCreative) {
    return null;
  }

  const advertsTrackingContext: TrackingContext = {
    idCreative: trackingContext.idCreative,
    idPlacement: trackingContext?.idPlacement ?? '',
    idFormat: trackingContext?.idFormat ?? '',
    idTracking: trackingContext?.idTracking ?? '',
  };

  if (trackingContext?.idView) {
    advertsTrackingContext.idResponse = trackingContext.idView;
  }

  return advertsTrackingContext;
};

export const transformAdsCapabilityToAdvertsTrackingInfo = (
  response: GetAdsResponse,
): AdvertTrackingInfo[] => {
  const advertsTrackingInfo: AdvertTrackingInfo[] = [];

  Object.values(response.slotAdverts ?? {}).forEach((adverts) => {
    (adverts.items ?? []).forEach((advert) => {
      const { creative } = advert;
      const creativeFields =
        creative?.homepageHero ?? creative?.homepageVideo ?? creative?.graphicPromo;

      if (!creativeFields) {
        return;
      }

      const trackingContext = transformAdsCapabilityTrackingContext(advert.trackingContext);

      if (!trackingContext) {
        return;
      }

      advertsTrackingInfo.push({
        creative: creativeFields,
        pixels: transformAdsCapabilityTrackingPixels(advert.trackingPixels),
        trackingContext,
        performanceEventsPayloads: advert.performanceEventsPayloads,
      });
    });
  });

  return advertsTrackingInfo;
};

export const transformAdsCapabilityExploreToAdvertsTrackingInfo = (
  response: GetAdsResponse,
): AdvertTrackingInfo[] => {
  const advertsTrackingInfo: AdvertTrackingInfo[] = [];

  Object.values(response.slotAdverts ?? {}).forEach((adverts) => {
    (adverts.items ?? []).forEach((advert) => {
      const creativeFields = advert.creative?.filteredExploreAirline;

      if (!creativeFields) {
        return;
      }

      const trackingContext = transformAdsCapabilityTrackingContext(advert.trackingContext);

      if (!trackingContext) {
        return;
      }

      advertsTrackingInfo.push({
        creative: creativeFields,
        pixels: transformAdsCapabilityTrackingPixels(advert.trackingPixels),
        trackingContext,
      });
    });
  });

  return advertsTrackingInfo;
};
